import { useState, useEffect, useRef } from 'preact/hooks';
import { useAsyncAction } from '../../hooks/use-async-action';
import { getHealth } from '../../api/health';
import { getErrors, clearErrors, getRateLimits, clearRateLimits } from '../../api/logs';
import { invalidateBarcode, flushAllCaches, resetDatabase, listKVKeys, getKVValue, deleteKVKey } from '../../api/cache';
import { Card } from '../../components/card';
import { Table, type Column } from '../../components/table';
import { Badge } from '../../components/badge';
import { Button } from '../../components/button';
import { Input } from '../../components/input';
import { Spinner } from '../../components/spinner';
import { EmptyState } from '../../components/empty-state';
import { Message } from '../../components/message';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DangerZone } from '../../components/danger-zone';
import type { ErrorLogEntry, RateLimitEntry, KVKey } from '../../api/types';

type SubTab = 'health' | 'logs' | 'cache';

function formatDate(iso: string | null): string {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Health Sub-Section ──
function HealthSection() {
  const mounted = useRef(false);
  const health = useAsyncAction(getHealth);
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    health.execute();
  }, []);

  const handleRefresh = async () => {
    setSpinning(true);
    await health.execute();
    setTimeout(() => setSpinning(false), 600);
  };

  const deps = health.result
    ? Object.entries(health.result.dependencies)
    : [];

  const allOk = deps.length > 0 && deps.every(([, v]) => v === 'ok');

  return (
    <Card>
      <div class="health-header">
        <div>
          <h2 style={{ marginBottom: 0 }}>Service Health</h2>
          {deps.length > 0 && (
            <span class={`system-status ${allOk ? 'system-status-ok' : 'system-status-degraded'}`} style={{ marginTop: '8px' }}>
              <span class="system-status-dot" />
              {allOk ? 'All systems operational' : 'Some services degraded'}
            </span>
          )}
        </div>
        <button class="btn-icon" onClick={handleRefresh} title="Refresh">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            style={spinning ? 'animation:spin 0.6s linear' : undefined}
          >
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
        </button>
      </div>

      <div style={{ marginTop: '16px' }}>
        {health.loading && deps.length === 0 ? (
          <Spinner text="Checking services" centered />
        ) : health.error ? (
          <EmptyState icon="error" message="Failed to load health status" />
        ) : (
          deps.map(([name, status]) => (
            <div class="health-row" key={name}>
              <span>{name}</span>
              <Badge
                label={status}
                variant={status === 'ok' ? 'ok' : 'error'}
              />
            </div>
          ))
        )}
      </div>
    </Card>
  );
}

// ── Logs Sub-Section ──
const errorColumns: Column<ErrorLogEntry>[] = [
  {
    header: 'Time',
    key: 'timestamp',
    render: (row) => <span class="date-cell">{formatDate(row.timestamp)}</span>,
  },
  {
    header: 'Path',
    key: 'path',
    render: (row) => <span class="mono-cell">{row.path}</span>,
  },
  {
    header: 'Message',
    key: 'message',
    render: (row) => (
      <span style={{ color: 'var(--red)', fontSize: '0.8rem' }}>{row.message}</span>
    ),
  },
  {
    header: 'Status',
    key: 'status',
    render: (row) => (
      <Badge label={row.status ? String(row.status) : '\u2014'} variant="error" />
    ),
  },
];

const rateLimitColumns: Column<RateLimitEntry>[] = [
  {
    header: 'Time',
    key: 'timestamp',
    render: (row) => <span class="date-cell">{formatDate(row.timestamp)}</span>,
  },
  {
    header: 'IP / User ID',
    key: 'userId',
    render: (row) => <span class="mono-cell">{row.userId}</span>,
  },
  {
    header: 'Path',
    key: 'path',
    render: (row) => <span class="mono-cell">{row.path}</span>,
  },
];

function LogsSection() {
  const mounted = useRef(false);
  const errors = useAsyncAction(getErrors);
  const rateLimits = useAsyncAction(getRateLimits);
  const clearErr = useAsyncAction(clearErrors);
  const clearRL = useAsyncAction(clearRateLimits);

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    errors.execute();
    rateLimits.execute();
  }, []);

  const handleClearErrors = async () => {
    await clearErr.execute();
    errors.execute();
  };

  const handleClearRateLimits = async () => {
    await clearRL.execute();
    rateLimits.execute();
  };

  const errorEntries = errors.result?.entries ?? [];
  const rlEntries = rateLimits.result?.entries ?? [];

  return (
    <div>
      <Card>
        <div class="section-head">
          <h2>Recent Errors</h2>
          <Button
            variant="danger"
            size="sm"
            onClick={handleClearErrors}
            disabled={clearErr.loading}
          >
            Clear
          </Button>
        </div>
        {errors.loading ? (
          <Spinner centered />
        ) : errorEntries.length > 0 ? (
          <Table columns={errorColumns} rows={errorEntries} />
        ) : (
          <EmptyState icon="inbox" message="No recent errors" />
        )}
      </Card>

      <Card>
        <div class="section-head">
          <h2>Rate Limit Events</h2>
          <Button
            variant="danger"
            size="sm"
            onClick={handleClearRateLimits}
            disabled={clearRL.loading}
          >
            Clear
          </Button>
        </div>
        {rateLimits.loading ? (
          <Spinner centered />
        ) : rlEntries.length > 0 ? (
          <Table columns={rateLimitColumns} rows={rlEntries} />
        ) : (
          <EmptyState icon="inbox" message="No rate limit events" />
        )}
      </Card>
    </div>
  );
}

// ── KV Browser Inline ──
function KVBrowserInline({ prefix }: { prefix: string }) {
  const [keys, setKeys] = useState<KVKey[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [expandedValue, setExpandedValue] = useState<string | null>(null);
  const [valueLoading, setValueLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const browse = async (nextCursor?: string) => {
    const isMore = !!nextCursor;
    if (isMore) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await listKVKeys({ prefix: prefix || undefined, cursor: nextCursor, limit: 50 });
      setKeys((prev) => isMore ? [...prev, ...res.keys] : res.keys);
      setCursor(res.list_complete ? undefined : res.cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load keys');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    browse();
  }, [prefix]);

  const handleView = async (key: string) => {
    if (expandedKey === key) {
      setExpandedKey(null);
      setExpandedValue(null);
      return;
    }
    setExpandedKey(key);
    setExpandedValue(null);
    setValueLoading(true);
    try {
      const res = await getKVValue(key);
      const val = typeof res.value === 'string' ? res.value : JSON.stringify(res.value, null, 2);
      setExpandedValue(val);
    } catch (e) {
      setExpandedValue(`Error: ${e instanceof Error ? e.message : 'Failed to load'}`);
    } finally {
      setValueLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteKVKey(deleteTarget);
      setKeys((prev) => prev.filter((k) => k.name !== deleteTarget));
      if (expandedKey === deleteTarget) {
        setExpandedKey(null);
        setExpandedValue(null);
      }
    } catch {
      // silent
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  if (loading) return <Spinner text="Loading keys" />;
  if (error) return <div class="msg msg-error">{error}</div>;
  if (keys.length === 0) return <EmptyState message="No KV keys found" />;

  return (
    <div>
      <div style={{ fontSize: '0.76rem', color: 'var(--text-tertiary)', marginBottom: '6px' }}>
        {keys.length} key{keys.length !== 1 ? 's' : ''} loaded
        {cursor ? ' (more available)' : ''}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
        {keys.map((k) => (
          <div key={k.name}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: expandedKey === k.name ? 'var(--bg-hover)' : 'var(--bg-raised)', borderRadius: 'var(--radius-xs)', fontSize: '0.8rem' }}>
              <span style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: '0.76rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {k.name}
              </span>
              <Button size="sm" variant="ghost" onClick={() => handleView(k.name)}>
                {expandedKey === k.name ? 'Hide' : 'View'}
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDeleteTarget(k.name)}>
                Delete
              </Button>
            </div>
            {expandedKey === k.name && (
              <div style={{ padding: '6px 10px', background: 'var(--bg-surface)', borderRadius: '0 0 var(--radius-xs) var(--radius-xs)', marginTop: '1px' }}>
                {valueLoading ? (
                  <Spinner text="Loading value" />
                ) : (
                  <pre style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, maxHeight: '260px', overflow: 'auto' }}>
                    <code>{expandedValue}</code>
                  </pre>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {cursor && (
        <div style={{ marginTop: '10px' }}>
          <Button variant="ghost" size="sm" onClick={() => browse(cursor)} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete KV Key"
        message={`Permanently delete key "${deleteTarget ?? ''}"?`}
        confirmLabel={deleteLoading ? 'Deleting...' : 'Delete'}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        isDestructive
      />
    </div>
  );
}

// ── Cache Sub-Section ──
function isProduction(): boolean {
  const host = window.location.hostname;
  return host !== 'localhost' && host !== '127.0.0.1' && !host.includes('staging') && !host.includes('stg');
}

function CacheSection() {
  const [barcode, setBarcode] = useState('');
  const invalidate = useAsyncAction((bc: string) => invalidateBarcode(bc));

  const handleInvalidate = () => {
    const trimmed = barcode.trim();
    if (!trimmed) return;
    invalidate.execute(trimmed);
  };

  const flush = useAsyncAction(flushAllCaches);
  const [flushConfirm, setFlushConfirm] = useState(false);

  const [kvPrefix, setKvPrefix] = useState('');
  const [activePrefix, setActivePrefix] = useState('');

  const handleBrowse = () => {
    setActivePrefix(kvPrefix);
  };

  const reset = useAsyncAction(resetDatabase);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetDoubleConfirm, setResetDoubleConfirm] = useState(false);

  const handleResetFirst = () => {
    setResetConfirm(false);
    setResetDoubleConfirm(true);
  };

  const handleResetFinal = async () => {
    setResetDoubleConfirm(false);
    await reset.execute();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Card>
        <h2 style={{ marginBottom: '10px' }}>Invalidate Barcode Cache</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <Input value={barcode} onChange={setBarcode} placeholder="Enter barcode..." />
          </div>
          <Button onClick={handleInvalidate} disabled={invalidate.loading || !barcode.trim()}>
            {invalidate.loading ? 'Invalidating...' : 'Invalidate'}
          </Button>
        </div>
        {invalidate.result && (
          <Message text={`Deleted ${invalidate.result.deleted} cache entries`} type="success" onDismiss={() => { }} />
        )}
        {invalidate.error && <Message text={invalidate.error} type="error" onDismiss={() => { }} />}
      </Card>

      <Card>
        <h2 style={{ marginBottom: '10px' }}>Flush All Caches</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '10px' }}>
          Clear all analysis and text caches. Products will be re-evaluated on next scan.
        </p>
        <Button variant="danger" onClick={() => setFlushConfirm(true)} disabled={flush.loading}>
          {flush.loading ? 'Flushing...' : 'Flush All Caches'}
        </Button>
        {flush.result && (
          <Message text={`Flushed ${flush.result.deleted} cache entries`} type="success" onDismiss={() => { }} />
        )}
        {flush.error && <Message text={flush.error} type="error" onDismiss={() => { }} />}
        <ConfirmDialog
          open={flushConfirm}
          title="Flush All Caches"
          message="This will delete all cached analysis results and text. Products will need to be re-evaluated on their next scan. Continue?"
          confirmLabel="Flush All"
          onConfirm={() => { setFlushConfirm(false); flush.execute(); }}
          onCancel={() => setFlushConfirm(false)}
          isDestructive
        />
      </Card>

      <Card>
        <h2 style={{ marginBottom: '10px' }}>KV Browser</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <Input value={kvPrefix} onChange={setKvPrefix} placeholder="Filter by prefix (optional)..." />
          </div>
          <Button variant="ghost" onClick={handleBrowse}>Browse</Button>
        </div>
        <KVBrowserInline prefix={activePrefix} />
      </Card>

      {!isProduction() && (
        <DangerZone
          title="Reset Everything"
          description="Wipe all database rows and KV cache entries. This cannot be undone."
          buttonLabel={reset.loading ? 'Resetting...' : 'Reset Database'}
          onAction={() => setResetConfirm(true)}
          confirmMessage="Are you sure you want to reset the entire database? All products, ingredients, verdicts, users, and cache entries will be permanently deleted."
        />
      )}

      <ConfirmDialog
        open={resetConfirm}
        title="Reset Database"
        message="Are you sure you want to reset the entire database? All products, ingredients, verdicts, users, and cache entries will be permanently deleted."
        confirmLabel="Yes, Continue"
        onConfirm={handleResetFirst}
        onCancel={() => setResetConfirm(false)}
        isDestructive
      />
      <ConfirmDialog
        open={resetDoubleConfirm}
        title="Final Confirmation"
        message="This is your last chance. This action is irreversible and will destroy all data. Are you absolutely sure?"
        confirmLabel="Reset Everything"
        onConfirm={handleResetFinal}
        onCancel={() => setResetDoubleConfirm(false)}
        isDestructive
      />
      {reset.result && (
        <Message text={`Database reset complete. ${reset.result.cacheDeleted} cache entries deleted.`} type="success" onDismiss={() => { }} />
      )}
      {reset.error && <Message text={reset.error} type="error" onDismiss={() => { }} />}
    </div>
  );
}


// ── Main System Tab ──
interface SystemTabProps {
  initialSubTab?: SubTab;
}

export function SystemTab({ initialSubTab = 'health' }: SystemTabProps) {
  const subTab = initialSubTab;
  const mounted = useRef(false);
  const health = useAsyncAction(getHealth);

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    health.execute();
  }, []);

  const healthData = health.result;
  const degradedCount = healthData
    ? Object.values(healthData.dependencies).filter((v) => v !== 'ok').length
    : 0;

  return (
    <div>
      {/* Health status banner */}
      {healthData ? (
        <div class={`system-health-banner ${degradedCount === 0 ? 'ok' : 'degraded'}`}>
          <span class="system-status-dot" style={{ width: '7px', height: '7px', borderRadius: '50%', background: degradedCount === 0 ? 'var(--green)' : '#f59e0b', boxShadow: degradedCount === 0 ? '0 0 6px rgba(5,150,105,0.4)' : 'none' }} />
          {degradedCount === 0 ? 'All systems operational' : `${degradedCount} service(s) degraded`}
        </div>
      ) : health.loading ? (
        <div class="system-health-banner unknown">
          <span class="spinner" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} />
          Checking system health...
        </div>
      ) : health.error ? (
        <div class="system-health-banner degraded">
          Unable to reach health endpoint
        </div>
      ) : null}

      {/* Sub-tab content — controlled by sidebar navigation */}
      {subTab === 'health' && <HealthSection />}
      {subTab === 'logs' && <LogsSection />}
      {subTab === 'cache' && <CacheSection />}
    </div>
  );
}
